if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.configuratorAddOns = document.querySelectorAll('[data-add-on-id]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      reinitialize(newNode) {
        this.configuratorAddOns = newNode.querySelectorAll('[data-add-on-id]');
      }

      get shouldRedirectToCart() {
        return (
          this.configuratorAddOns.length > 0 || document.querySelector('[data-redirect-cart]') != null
        );
      }

      setLoadingSpinnerVisible(visible) {
        const dawnSpinner = this.submitButton.querySelector('.loading__spinner');
        const legacySpinner = this.submitButton.querySelector('.loading-overlay__spinner');

        if (dawnSpinner) dawnSpinner.classList.toggle('hidden', !visible);
        if (legacySpinner) legacySpinner.classList.toggle('d-none', !visible);
      }

      onSubmitHandler(evt) {
        // Do not remove: without preventDefault, native POST to /cart/add causes cart-page redirect + double-add (Dawn upstream sync has clobbered this before).
        evt.preventDefault();
        if (this.submitButton.classList.contains('loading')) return;
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');
        this.setLoadingSpinnerVisible(true);

        const config = fetchConfig(this.shouldRedirectToCart ? 'json' : 'javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);

        if (this.shouldRedirectToCart) {
          const items = [];
          this.configuratorAddOns.forEach((product) => {
            if (!product.checked) return;
            items.push({ id: product.dataset.addOnId, quantity: 1 });
          });
          items.push({ id: formData.get('id'), quantity: formData.get('quantity') || 1 });
          config.body = JSON.stringify({ items });
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          config.body = formData;
        }

        if (this.cart) this.cart.setActiveElement(document.activeElement);

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => response.json())
          .then(async (response) => {
            if (response.handle) {
              await this.handleFreeProduct();
            }

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText?.classList.add('hidden');
              this.submitButton.querySelector('span')?.classList.add('d-none');
              soldOutMessage.classList.remove('hidden', 'd-none');
              this.error = true;
              return;
            }

            if (this.shouldRedirectToCart) {
              this.error = false;
              return;
            }

            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            if (this.shouldRedirectToCart && !this.error) {
              window.location.href = window.routes.cart_url;
            }
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-busy');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.setLoadingSpinnerVisible(false);

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      async handleFreeProduct() {
        try {
          const cartRes = await fetch('/cart.js');
          const cart = await cartRes.json();

          const configEl = document.getElementById('cart-reward-config');
          if (!configEl) return false;
          const offerEl = document.getElementById('cart-threshold-offer');
          if (!offerEl) return false;

          const { threshold, reward_type, free_variant_id } = JSON.parse(configEl.textContent || '{}');

          if (reward_type !== 'free_product' || !free_variant_id) return false;

          const cartTotal = cart.total_price;
          const reached = cartTotal >= threshold;
          const freeItem = cart.items.find((i) => i.id === free_variant_id);

          if (reached && !freeItem) {
            return false;
          }

          if (!reached && freeItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: freeItem.key, quantity: 0 }),
            });
            return true;
          }

          return false;
        } catch (err) {
          console.error('[CartReward] Error:', err);
          return false;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
